import {
  Alert,
  Anchor,
  Box,
  Card,
  CloseButton,
  Combobox,
  FormField,
  FormHelp,
  Heading,
  Icon,
  Input,
  Label,
  Paragraph,
  Select,
  Textarea,
  TextButton,
} from '@zillow/constellation-10';
import { IconHouseFilled } from '@zillow/constellation-icons';
import { Controller, useFormContext } from 'react-hook-form';
import { useCappsResidenceWebApiHandler_validateAddress } from '@zg-rentals/ts-capps-client-react-query';
import { get } from 'lodash-es';
import { AddressAutocomplete, type AddressResponse } from '../../../AddressAutocomplete';
import { useAddressAutocomplete } from '../../../../hooks/useAddressAutocomplete';
import { FIELD_IDS } from '../../../../constants/wizard';
import { HomeType } from '@zg-rentals/ts-capps-client';
import { MaskedCurrencyInput, MaskedPhoneInput, MaskedZipCodeInput, validateZipCode } from '../../../Inputs';
import { date50YearsAgo, isValidUSPhoneNumber, today } from '../../../../utils/formatters';
import { useEffect, useRef, useState } from 'react';
import { TEXT_AREA_MAX_LENGTH_510 } from '../../../../constants/form';
import { US_STATES_ABBR_LIST_EXCLUDING_TERRITORIES } from '../../../../constants/applicationsConstants';
import { validateDate, validateEmail } from '../../../../utils/validation';
import { DatePicker } from '../../../DatePicker';
import { validateCurrency } from '../../../Inputs/MaskedCurrencyInput';
import { getFormattedAddress } from '@zg-rentals/applications-utils';

export function CurrentResidenceStep() {
  const {
    formState: { errors, submitCount },
    register,
    setValue,
    trigger,
    watch,
  } = useFormContext();

  const errorMonthlyPayment = get(errors, 'currentResidence.monthlyPayment');
  const homeType = watch(FIELD_IDS.CURRENT_RESIDENCE_TYPE);
  const stateValue = watch(FIELD_IDS.CURRENT_RESIDENCE_STATE);
  const zpid = watch(FIELD_IDS.CURRENT_RESIDENCE_ZPID);

  // isZipOutsideUsStates is reported by CurrentAddress from the validateAddress API result.
  const [isZipOutsideUsStates, setIsZipOutsideUsStates] = useState(false);
  const isStateOutsideUsStates =
    !!stateValue && !US_STATES_ABBR_LIST_EXCLUDING_TERRITORIES.includes(stateValue.toUpperCase());
  const isAddressOutsideUsStates = isStateOutsideUsStates || isZipOutsideUsStates;

  const [isTerritoryNoticeDismissed, setIsTerritoryNoticeDismissed] = useState(false);

  // submitCount is shared across all wizard steps, so compare against the mount value to detect a Next click here.
  const submitCountOnMount = useRef(submitCount);
  const hasTriedToContinue = submitCount > submitCountOnMount.current;

  const showTerritoryNotice = isAddressOutsideUsStates && hasTriedToContinue && !isTerritoryNoticeDismissed;

  // Re-show a dismissed notice on each new continue attempt or address selection.
  useEffect(() => {
    setIsTerritoryNoticeDismissed(false);
  }, [zpid, submitCount]);

  useEffect(() => {
    // Default type to rent if none is set initially
    if (!homeType) {
      setValue(FIELD_IDS.CURRENT_RESIDENCE_TYPE, HomeType.RENT);
      trigger(FIELD_IDS.CURRENT_RESIDENCE_TYPE);
    }
  }, [homeType, setValue, trigger]);

  return (
    <>
      <Heading level={2} css={{ textStyle: 'heading-sm', fontFamily: 'heading-alt', marginBottom: 'sm' }}>
        Current residence
      </Heading>
      {showTerritoryNotice && (
        <Alert
          tone="warning"
          role="alert"
          closeButton={<CloseButton onClick={() => setIsTerritoryNoticeDismissed(true)} />}
          css={{ marginBottom: 'sm' }}
        >
          We’re unable to process applications from outside the United States. Enter an address in one of the 50 states
          or Washington, D.C. to continue.
        </Alert>
      )}
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'sm' }}>
        <Controller
          name="currentResidence.type"
          render={({ field: { onChange, value, onBlur } }) => (
            <FormField
              label={<Label>Type</Label>}
              // @ts-expect-error inferred type is incorrect
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                let fieldsToClear: Array<string> = [];
                if (e.target.value !== HomeType.RENT) {
                  fieldsToClear = [
                    ...fieldsToClear,
                    'currentResidence.manager.name',
                    'currentResidence.manager.email',
                    'currentResidence.manager.phone',
                  ];
                }
                if (e.target.value !== HomeType.OTHER) {
                  fieldsToClear = [...fieldsToClear, FIELD_IDS.CURRENT_RESIDENCE_DESCRIPTION];
                }
                if (e.target.value === HomeType.OTHER) {
                  fieldsToClear = [...fieldsToClear, FIELD_IDS.REASON_FOR_MOVING];
                }
                fieldsToClear.forEach((fieldToClear) => setValue(fieldToClear, null));
                onChange(e.target.value);
              }}
              control={
                <Select value={value || ''} onBlur={onBlur}>
                  <option value={HomeType.RENT}>A home I rent</option>
                  <option value={HomeType.OWN}>A home I own</option>
                  <option value={HomeType.OTHER}>A different type of home</option>
                </Select>
              }
            />
          )}
        />
        <CurrentAddress onZipTerritoryChange={setIsZipOutsideUsStates} />
        <Controller
          name="currentResidence.startDate"
          rules={{
            required: 'A moved-in date is required',
            validate: (v) =>
              validateDate(v, {
                inThePast: true,
                maxYearsAgo: 50,
              }),
          }}
          render={({ field, fieldState: { error, invalid } }) => (
            <FormField
              label={<Label id="moved-in-label">Moved in</Label>}
              description={error && <FormHelp>{error?.message}</FormHelp>}
              error={invalid}
            >
              <DatePicker {...field} minDate={date50YearsAgo()} maxDate={today()} aria-labelledby="moved-in-label" />
            </FormField>
          )}
        />
        <Controller
          name={FIELD_IDS.CURRENT_RESIDENCE_AMOUNT}
          rules={{ required: 'Monthly amount is required', validate: validateCurrency }}
          render={({ field }) => (
            <FormField
              label={<Label id="monthly-amount-label">Monthly {homeType === HomeType.OWN ? 'mortgage' : 'rent'}</Label>}
              control={<MaskedCurrencyInput {...field} aria-labelledby="monthly-amount-label" />}
              description={errorMonthlyPayment && <FormHelp>{String(errorMonthlyPayment.message)}</FormHelp>}
              error={!!errorMonthlyPayment}
            />
          )}
        />
        {(homeType === HomeType.RENT || homeType === HomeType.OWN) && (
          <FormField
            label={<Label>Reason for moving</Label>}
            control={
              <Textarea
                maxLength={TEXT_AREA_MAX_LENGTH_510}
                {...register(FIELD_IDS.REASON_FOR_MOVING, {
                  required: 'A reason for moving is required',
                })}
                css={{ resize: 'vertical' }}
              />
            }
            description={errors.reasonForMoving && <FormHelp>{String(errors.reasonForMoving.message)}</FormHelp>}
            error={!!errors.reasonForMoving}
          />
        )}
        {homeType === HomeType.RENT && <LandlordContact />}
        {homeType === HomeType.OTHER && (
          <FormField
            label={<Label>Description</Label>}
            control={<Textarea {...register(FIELD_IDS.CURRENT_RESIDENCE_DESCRIPTION)} css={{ resize: 'vertical' }} />}
            optional
          />
        )}
      </Box>
    </>
  );
}

type CurrentAddressProps = {
  onZipTerritoryChange: (isTerritory: boolean) => void;
};

function CurrentAddress({ onZipTerritoryChange }: CurrentAddressProps) {
  const {
    formState: { errors },
    setValue,
    register,
    trigger,
    watch,
  } = useFormContext();

  const { mutateAsync: validateAddressMutation } = useCappsResidenceWebApiHandler_validateAddress();

  // Identifies the most recent address check so out-of-order async responses don't overwrite a newer result.
  const latestZipCheckId = useRef(0);

  const validateZip = async (value: string) => {
    const formatResult = validateZipCode(value);
    if (formatResult !== true) {
      latestZipCheckId.current += 1;
      onZipTerritoryChange(false);
      return formatResult;
    }
    if (!cityValue || !stateValue) {
      latestZipCheckId.current += 1;
      onZipTerritoryChange(false);
      return true;
    }
    const checkId = (latestZipCheckId.current += 1);
    try {
      const { isValid, errorMessage } = await validateAddressMutation({
        getParams: { city: cityValue, state: stateValue, zipcode: value },
      });
      if (checkId === latestZipCheckId.current) {
        onZipTerritoryChange(!isValid);
      }
      return isValid || errorMessage || 'Enter a ZIP code in one of the 50 states or Washington, D.C. to continue';
    } catch {
      // Don't block the applicant on a network/server error.
      if (checkId === latestZipCheckId.current) {
        onZipTerritoryChange(false);
      }
      return true;
    }
  };

  const validateAddressState = (state?: string | null) => {
    if (!state || US_STATES_ABBR_LIST_EXCLUDING_TERRITORIES.includes(state.toUpperCase())) {
      return true;
    }
    return 'Enter an address in one of the 50 states or Washington, D.C. to continue';
  };

  const zpid = watch(FIELD_IDS.CURRENT_RESIDENCE_ZPID);
  const streetValue = watch(FIELD_IDS.CURRENT_RESIDENCE_STREET);
  const cityValue = watch(FIELD_IDS.CURRENT_RESIDENCE_CITY);
  const stateValue = watch(FIELD_IDS.CURRENT_RESIDENCE_STATE);
  const zipValue = watch(FIELD_IDS.CURRENT_RESIDENCE_ZIP);

  const addressValue = getFormattedAddress(
    streetValue
      ? {
          street: streetValue,
          city: cityValue,
          state: stateValue,
          zip: zipValue,
        }
      : undefined,
  );

  // If the user has an address entered previously that was not validated, show the manual address form
  const [isManualAddress, setIsManualAddress] = useState(!zpid && streetValue);
  const [isEditingAddress, setIsEditingAddress] = useState(!streetValue);

  // validateAddress runs as part of ZIP validation, but it depends on city and state too.
  // Re-trigger it when city or state changes on an already-entered ZIP, so switching to the
  // manual form (empty ZIP) doesn't surface the required error before the user has typed.
  useEffect(() => {
    if (isManualAddress && zipValue) {
      trigger(FIELD_IDS.CURRENT_RESIDENCE_ZIP);
    }
  }, [cityValue, stateValue, isManualAddress, zipValue, trigger]);

  const [partialAddress, setPartialAddress, queryData] = useAddressAutocomplete({});

  const errorStreet = get(errors, FIELD_IDS.CURRENT_RESIDENCE_STREET);
  const errorCity = get(errors, FIELD_IDS.CURRENT_RESIDENCE_CITY);
  const errorState = get(errors, FIELD_IDS.CURRENT_RESIDENCE_STATE);
  const errorZip = get(errors, FIELD_IDS.CURRENT_RESIDENCE_ZIP);

  function onEditAddress() {
    setIsEditingAddress(true);
    onZipTerritoryChange(false);

    setValue(FIELD_IDS.CURRENT_RESIDENCE_ZPID, null);
    setValue(FIELD_IDS.CURRENT_RESIDENCE_STREET, null);
    setValue(FIELD_IDS.CURRENT_RESIDENCE_CITY, null);
    setValue(FIELD_IDS.CURRENT_RESIDENCE_STATE, null);
    setValue(FIELD_IDS.CURRENT_RESIDENCE_ZIP, null);
  }

  function onClickManualAddress() {
    setValue(FIELD_IDS.CURRENT_RESIDENCE_STREET, partialAddress);
    trigger(FIELD_IDS.CURRENT_RESIDENCE_STREET);
    setIsManualAddress(true);
  }

  return (
    <>
      <Controller
        name={FIELD_IDS.CURRENT_RESIDENCE_STREET}
        rules={{
          required: 'A USPS-validated address is required',
          validate: (_value, formValues) => validateAddressState(get(formValues, FIELD_IDS.CURRENT_RESIDENCE_STATE)),
        }}
        render={({ field: { onChange, value, ...rest } }) => {
          const handleAddressSelect = (address: AddressResponse) => {
            setIsEditingAddress(false);
            onZipTerritoryChange(false);
            setValue(FIELD_IDS.CURRENT_RESIDENCE_ZPID, Number(address.zpid));
            onChange(address.street);
            setValue(FIELD_IDS.CURRENT_RESIDENCE_CITY, address.city);
            setValue(FIELD_IDS.CURRENT_RESIDENCE_STATE, address.state);
            setValue(FIELD_IDS.CURRENT_RESIDENCE_ZIP, address.zip);
            trigger(FIELD_IDS.CURRENT_RESIDENCE_STREET);
          };

          return (
            <FormField
              label={<Label id="current-residence-address-label">{isManualAddress ? 'Street' : 'Address'}</Label>}
              disabled={!isManualAddress && !isEditingAddress}
              control={
                !isManualAddress ? (
                  <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                    <Box css={{ display: 'block', flexGrow: '1' }}>
                      <AddressAutocomplete
                        {...rest}
                        value={addressValue}
                        partialAddress={partialAddress}
                        onSetPartialAddress={setPartialAddress}
                        queryData={queryData}
                        onAddressSelect={handleAddressSelect}
                        renderEmptyState={
                          <Paragraph>
                            Address not found. If you are at an address that is not found,{' '}
                            <Anchor asChild>
                              <button type="button" onClick={onClickManualAddress}>
                                enter it manually
                              </button>
                            </Anchor>
                          </Paragraph>
                        }
                        error={!!errorStreet}
                        disabled={!isEditingAddress}
                        aria-labelledby="current-residence-address-label"
                      />
                    </Box>
                    {!isEditingAddress && (
                      <Box css={{ marginLeft: 'sm' }}>
                        <TextButton onClick={onEditAddress}>Edit</TextButton>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Input value={value} onChange={onChange} aria-labelledby="current-residence-address-label" />
                )
              }
              description={errorStreet && <FormHelp>{String(errorStreet.message)}</FormHelp>}
              error={!!errorStreet}
            />
          );
        }}
      />
      {isManualAddress && (
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tight' }}>
          <FormField
            label={<Label>City</Label>}
            control={
              <Input
                maxLength={32}
                {...register(FIELD_IDS.CURRENT_RESIDENCE_CITY, {
                  required: 'City is required',
                })}
              />
            }
            description={errorCity && <FormHelp>{String(errorCity.message)}</FormHelp>}
            error={!!errorCity}
          />
          <Controller
            name="currentResidence.address.state"
            rules={{ required: 'State is required' }}
            render={({ field }) => (
              <FormField
                label={<Label id="state-label">State</Label>}
                control={
                  <Combobox
                    {...field}
                    value={field.value || ''}
                    placeholder="Select a state"
                    options={US_STATES_ABBR_LIST_EXCLUDING_TERRITORIES}
                    size="md"
                    aria-labelledby="state-label"
                  />
                }
                description={errorState && <FormHelp>{String(errorState.message)}</FormHelp>}
                error={!!errorState}
              />
            )}
          />
          <FormField
            label={<Label id="zip-label">ZIP code</Label>}
            control={
              <Controller
                name="currentResidence.address.zip"
                rules={{
                  required: 'ZIP code is required',
                  validate: validateZip,
                }}
                render={({ field }) => <MaskedZipCodeInput {...field} aria-labelledby="zip-label" />}
              />
            }
            description={errorZip && <FormHelp>{String(errorZip.message)}</FormHelp>}
            error={!!errorZip}
          />
        </Box>
      )}
      <Alert tone="info" css={{ marginTop: 'tight' }}>
        Sales tax may be added at checkout depending on your address
      </Alert>
    </>
  );
}

export function CurrentResidenceStepTip() {
  return (
    <Card elevated={false}>
      <Icon role="img" size="lg" css={{ marginBottom: 'default' }}>
        <IconHouseFilled />
      </Icon>
      <Heading level={3} css={{ textStyle: 'heading-sm', fontFamily: 'heading-alt', marginBottom: 'sm' }}>
        Your residence history
      </Heading>

      <Paragraph>
        Rental managers like to know where you’ve lived and have an opportunity to check your references.
      </Paragraph>
      <Paragraph>Rental managers may use this to perform reference checks.</Paragraph>
    </Card>
  );
}

function LandlordContact() {
  const {
    formState: { errors },
    register,
  } = useFormContext();

  const errorLandlordName = get(errors, 'currentResidence.manager.name');
  const errorLandlordEmail = get(errors, 'currentResidence.manager.email');
  const errorLandlordPhone = get(errors, 'currentResidence.manager.phone');

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tight' }}>
      <FormField
        label={<Label>Landlord name</Label>}
        control={
          <Input
            maxLength={100}
            {...register('currentResidence.manager.name', {
              required: 'A name is required',
            })}
          />
        }
        description={errorLandlordName && <FormHelp>{String(errorLandlordName.message)}</FormHelp>}
        error={!!errorLandlordName}
      />
      <FormField
        label={<Label>Landlord contact email</Label>}
        control={
          <Input
            maxLength={100}
            {...register('currentResidence.manager.email', {
              validate: validateEmail,
            })}
            type="email"
          />
        }
        description={errorLandlordEmail && <FormHelp>{String(errorLandlordEmail.message)}</FormHelp>}
        error={!!errorLandlordEmail}
        optional
      />
      <FormField
        label={<Label id="landlord-phone-label">Landlord contact phone</Label>}
        control={
          <Controller
            name="currentResidence.manager.phone"
            rules={{
              required: 'A phone number is required',
              validate: (phone) => isValidUSPhoneNumber(phone) || 'Enter a valid phone number',
            }}
            render={({ field }) => <MaskedPhoneInput {...field} aria-labelledby="landlord-phone-label" />}
          />
        }
        description={errorLandlordPhone && <FormHelp>{String(errorLandlordPhone.message)}</FormHelp>}
        error={!!errorLandlordPhone}
      />
    </Box>
  );
}
